import React, { FC } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import Select from 'react-select'
import { ContentItem } from '../core/_models'

type MoveAction = 'before' | 'after' | 'position'

interface MoveChapterFormValues {
  sourceChapterId: string
  action: MoveAction
  targetChapterId: string
  targetPosition: string
}

interface ModalMoveChapterProps {
  show: boolean
  onHide: () => void
  onSubmit: () => void
  onValueChange: (name: keyof MoveChapterFormValues, value: string) => void
  values: MoveChapterFormValues
  chapters: ContentItem[]
  isLoading: boolean
  error?: string | null
}

const describe = (content: ContentItem, index: number) =>
  `${index + 1}. ${content.chapter_name || '-'}/${content.chapter_number || '-'} (${content.chapter_id || '-'
  }) — ${content.article_name || '-'}/${content.article_number || '-'} (${content.article_id || '-'})`

const ModalMoveChapter: FC<ModalMoveChapterProps> = ({
  show,
  onHide,
  onSubmit,
  onValueChange,
  values,
  chapters,
  isLoading,
  error,
}) => {
  // Agreements run to hundreds of articles, so both pickers are searchable —
  // scrolling a plain <select> that long is what drove people to the one-step
  // arrow buttons instead.
  const options = chapters.map((content, index) => ({
    value: String(content.id),
    label: describe(content, index),
  }))

  const byPosition = values.action === 'position'
  const position = Number(values.targetPosition)
  const isPositionValid =
    Number.isInteger(position) && position >= 1 && position <= chapters.length

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Move Article</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}

        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>Article to move</Form.Label>
            <Select
              options={options}
              value={options.find((o) => o.value === values.sourceChapterId) ?? null}
              onChange={(option) => onValueChange('sourceChapterId', option?.value ?? '')}
              placeholder='-- Select article --'
              isClearable
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Move position</Form.Label>
            <Form.Select
              value={values.action}
              onChange={(e) => onValueChange('action', e.target.value)}
            >
              <option value='before'>Before article</option>
              <option value='after'>After article</option>
              <option value='position'>To position number</option>
            </Form.Select>
          </Form.Group>

          {byPosition ? (
            <Form.Group>
              <Form.Label>Position</Form.Label>
              <Form.Control
                type='number'
                min={1}
                max={chapters.length}
                value={values.targetPosition}
                onChange={(e) => onValueChange('targetPosition', e.target.value)}
                placeholder={`1 - ${chapters.length}`}
              />
              <Form.Text muted>
                Where the article ends up in the list, counting from the top.
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Label>Target Article</Form.Label>
              <Select
                options={options}
                value={options.find((o) => o.value === values.targetChapterId) ?? null}
                onChange={(option) => onValueChange('targetChapterId', option?.value ?? '')}
                placeholder='-- Select article --'
                isClearable
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='default' className='btn-cancel' onClick={onHide}>
          Cancel
        </Button>

        <Button
          type='button' // IMPORTANT so it actually works
          variant='primary'
          onClick={onSubmit}
          disabled={
            isLoading ||
            !values.sourceChapterId ||
            (byPosition
              ? !isPositionValid
              : !values.targetChapterId ||
              values.sourceChapterId === values.targetChapterId)
          }
        >
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalMoveChapter
